import React, { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useReactiveVar } from '@apollo/client';

import { EcpLocator as EcpLocatorView } from './ecp-locator';
import { EcpServices } from './ecp-locator.models';
import { ChooseService } from './chose-service';
import { useMyLocation } from '../../../../hooks/use-my-location';
import { useSetFilterParams } from '../../../../graphql/ecp-locator/actions/set-filter-params';
import {
  filterParams,
  setAutocompletedSearch,
  setAutocompletedValue,
  setExternalSource,
  setGeoLocationReady,
  setUserLocation,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { GET_IS_USER_LOGGED_IN } from '../../../../graphql';
import { isUserFitted } from '../../../../utils/lenses/is-user-fitted';
import { GET_ECP_PROFILE } from '../../../../graphql/ecp-locator/queries';
import { EcpProfileResponse } from '../../../../graphql/ecp-locator/models';
import { getActiveAppointment } from '../../../../utils';

export const EcpLocator = (): JSX.Element => {
  const { data: { user: { isLoggedIn } } } = useQuery(GET_IS_USER_LOGGED_IN);
  const { data, loading, error, refetch } = useQuery<EcpProfileResponse>(GET_ECP_PROFILE, { fetchPolicy: 'network-only', skip: !isLoggedIn });
  const isFitted = isUserFitted(data);
  const activeAppointment = useMemo(() => {
    if (loading) {
      return undefined;
    }

    return getActiveAppointment(data?.profile?.appointment ?? []) ?? null;
  }, [data, error, loading]);

  // Get search params
  const [searchParams] = useSearchParams();
  const urlAddress = searchParams.get('address');
  const urlCity = searchParams.get('city');
  const urlSource = searchParams.get('source');
  // Get ECP service
  const ecpService = searchParams.get('ecpService') as EcpServices | null;
  const hasExternalAddress = !!(searchParams.get('address')?.trim());
  const showLocator = ecpService || hasExternalAddress;

  const { isLocationDefined, isResolved, coords } = useMyLocation();
  const { setFilterParamsToCache } = useSetFilterParams();
  const currentFilters = useReactiveVar(filterParams);
  const addressHandled = useRef(false);

  // Handle external address from URL query params (e.g. OZON store cards)
  useEffect(() => {
    if (!hasExternalAddress || addressHandled.current) return;
    addressHandled.current = true;

    setFilterParamsToCache({
      ...currentFilters,
    });

    if (urlSource) {
      setExternalSource(urlSource);
    }

    const searchQuery = urlCity
      ? `${urlCity.trim()}, ${urlAddress!.trim()}`
      : urlAddress!.trim();

    setAutocompletedValue(searchQuery);
    setAutocompletedSearch(true);
  }, []);

  // All cases without external address — only set params; EcpSearch will call updateStores
  useEffect(() => {
    if (hasExternalAddress || !isResolved) return;

    if (isLocationDefined) {
      setFilterParamsToCache(currentFilters);
      setUserLocation(coords as { latitude: number; longitude: number });
      setGeoLocationReady(true);
    } else {
      // Geo was denied or unavailable — trigger a search with current filters and no coords
      setFilterParamsToCache(currentFilters);
    }
  }, [isResolved]);

  return (
    <>
      {!showLocator && (
        <ChooseService
          isFitted={isFitted}
          isLoading={loading && isLocationDefined}
          ecpProfile={data}
          isLoggedIn={isLoggedIn}
          appointment={activeAppointment}
        />
      )}
      {showLocator && (
        <EcpLocatorView
          appointment={activeAppointment}
          isLoading={loading && isLocationDefined}
          isLoggedIn={isLoggedIn}
          isFitted={isFitted}
          hasExternalAddress={hasExternalAddress}
          isResolved={isResolved}
          refetchProfile={isLoggedIn ? refetch : null}
        />
      )}
    </>
  );
};
